import { FC, useState, useEffect } from "react";
import { styled } from "styled-components";
import { Entry } from "contentful";
import { font } from "../lib/sharedStyles";
import { ContentfulCategoryEntry } from "../lib/contentfulTypes";
import { SwiperCarousel, SwiperSlide } from "./Swiper";
import CategoryWrapper from "./CategoryWrapper";
import CategoryPill from "./CategoryPill";
import CategoryCard from "./CategoryCard";
import CategoryTile from "./CategoryTile";
import CategoryModelTile from "./CategoryModelTile";
import CategoryModelCard from "./CategoryModelCard";

interface CategoryRailEntry extends Entry {
  id: string;
  brand: string;
  fields: {
    categories: ContentfulCategoryEntry[];
    display: string;
    heading?: string;
    loopCarousel?: boolean;
    slideBackground?: string;
    slideHoverBackground?: string;
  }
}

const Rail = styled.div`
  padding: 40px 0;

  // Heading implemented this way to ensure
  // priority over marketing page p styles.
  > p {
    ${font('Futura', 600, '1.5rem', '1.875rem')}
    letter-spacing: -0.02em;
    color: #191A1F;
    margin-bottom: 32px;

    @media (min-width: 768px) {
      font-size: 2rem;
      line-height: 2.5rem;
    }
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;

  .swiper-container swiper-slide {
    width: auto;
  }

  &:hover {
    .swiper-button {
      opacity: 1;
      pointer-events: all;
    }
  }
`;

const ArrowWrap = styled.button<{$direction: string; $railType: string; $railId: string;}>`
  opacity: 0;
  pointer-events: none;
  position: absolute;
  z-index: 1;
  display: flex;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: opacity 300ms ease;

  ${({ $railType }) => $railType == 'Pill' && `
    top: 4px;
    display: none;

    @media (min-width: 768px) {
      display: flex;
    }
  `}

  ${({ $railType }) => ($railType == 'Card') && `
    top: 14px;
  `}

  ${({ $railType }) => ($railType == 'Tile') && `
    top: 84px;

    @media (min-width: 768px) {
      top: 10vw;
    }

    @media (min-width: 1024px) {
      top: 7vw;
    }
  `}

  ${({ $railType }) => $railType == 'ModelCard' && `
    display: sticky;
  `}

  ${({ $railType }) => $railType == 'ModelTile' && `
    opacity: 1 !important;
    pointer-events: all;
  `}

  ${({ $direction, $railType }) => $direction == 'left' && $railType == 'Pill' && `
    left: 20px;
  `}

  ${({ $direction, $railType }) => $direction == 'left' && ($railType == 'Card') && `
    left: 30px;
  `}

  ${({ $direction }) => $direction == 'right' && `
    transform: rotate(180deg);
  `}

  ${({ $direction, $railType }) => $direction == 'right' && $railType != 'Tile' && $railType != 'ModelTile' && `
    right: 5vw;

    @media (min-width: 1024px) {
      right: 3vw;
    }
  `}

  ${({ $direction, $railType }) => $direction == 'right' && ($railType == 'Tile') && `
    right: 1vw;
  `}

  ${({ $direction, $railType }) => $direction == 'right' && ($railType == 'ModelTile' || $railType == 'ModelCard') && `
    z-index: 4;
    top: -64px;
    right: 16px;
    pointer-events: all;
  `}

  ${({ $direction, $railType }) => $direction == 'left' && ($railType == 'ModelTile' || $railType == 'ModelCard') && `
    z-index: 4;
    top: -64px;
    right: 64px;
    pointer-events: all;
  `}
`;

const RailArrow: FC<{ railType: string; direction: string; railId: string }> = ({
  railType,
  direction,
  railId
}) => (
  <ArrowWrap $direction={direction} $railType={railType} $railId={railId} className={`swiper-button swiper-button-${direction}-${railId}`}>
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none">
      <rect width="32" height="32" x="32" y="32" fill="#fff" rx="16" transform="rotate(-180 32 32)" />
      <path fill="#191A1F" d="M21 16.75a.75.75 0 0 0 0-1.5v1.5Zm-10.53-1.28a.75.75 0 0 0 0 1.06l4.773 4.773a.75.75 0 0 0 1.06-1.06L12.061 16l4.242-4.243a.75.75 0 0 0-1.06-1.06L10.47 15.47ZM21 15.25H11v1.5h10v-1.5Z" />
    </svg>
  </ArrowWrap>
);

const CategoryRail: FC<CategoryRailEntry> = ({
  id,
  sys,
  fields: {
    categories,
    display,
    heading,
    loopCarousel,
    slideBackground,
    slideHoverBackground
  }
}) => {
  const [maxSlideHeight, setMaxSlideHeight] = useState(0);

  useEffect(() => {
    // Wait for slides to render
    const slides = document.querySelectorAll(`[data-rail-id="${sys.id}"] .swiper-slide`);
    if (slides.length) {
      const heights = Array.from(slides).map(slide => slide.scrollHeight);
      setMaxSlideHeight(Math.max(...heights));
    }
  }, [categories, sys.id]);

  let SlideComponent = null;
  let slidesPerViewConfig = {};

  if (display == 'Pill') {
    SlideComponent = CategoryPill;
    slidesPerViewConfig = {
      0: { slidesPerView: "auto" }
    };
  } else if (display == 'Card') {
    SlideComponent = CategoryCard;
    slidesPerViewConfig = {
      768: { slidesPerView: 3.25 },
      1024: { slidesPerView: 4.25 },
    };
  } else if (display == 'Tile') {
    SlideComponent = CategoryTile;
    slidesPerViewConfig = {
      768: { slidesPerView: 3.25 },
      1024: { slidesPerView: 3.25 },
    };
  } else if (display == 'ModelTile') {
    SlideComponent = CategoryModelTile;
    slidesPerViewConfig = {
      768: { slidesPerView: 3.25 },
      1440: { slidesPerView: 4.25 },
    };
  } else if (display == 'ModelCard') {
    SlideComponent = CategoryModelCard;
    slidesPerViewConfig = {
      0: { slidesPerView: "auto" },
    };
  }

  if (!SlideComponent) return null;

  const centeredSlides = display == 'ModelTile';
  const centerInsufficientSlides = categories.length < 3;
  const freeMode = !centeredSlides && !centerInsufficientSlides;

  return (
    <Rail data-component-id={id}>
      {heading && <p>{heading}</p>}
      <CarouselContainer data-rail-id={sys.id}>
        <SwiperCarousel
          className="swiper-container"
          centeredSlides={categories.length < 3 ? false : centeredSlides}
          centerInsufficientSlides={centerInsufficientSlides}
          loop={categories.length < 3 ? false : loopCarousel}
          freeMode={freeMode}
          spaceBetween={8}
          slidesOffsetBefore={16}
          slidesOffsetAfter={16}
          breakpoints={slidesPerViewConfig}
          navigation={{
            nextEl: `.swiper-button-right-${sys.id}`,
            prevEl: `.swiper-button-left-${sys.id}`
          }}
          mousewheel={{forceToAxis: true}}
        >
          {categories.map((category, index) => (
            <SwiperSlide key={`${category.sys.id}-${index}`} style={{ height: maxSlideHeight || "auto" }}>
              <CategoryWrapper
                category={category}
                Component={SlideComponent}
                slideBackground={slideBackground}
                slideHoverBackground={slideHoverBackground}
              />
            </SwiperSlide>
          ))}
        </SwiperCarousel>
        {categories.length > 3 && <>
          <RailArrow railType={display} direction="left" railId={sys.id} />
          <RailArrow railType={display} direction="right" railId={sys.id} />
        </>}
      </CarouselContainer>
    </Rail>
  );
}

export default CategoryRail;
